import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../../../auth/useUser';
import { generateLogos, refineLogo, uploadLogo, downloadLogoBundle, configureAuth, setAuthenticated, LimitExceededError, EmailNotVerifiedError } from '../../../services/logoApi';

const GeneratorContext = createContext(null);

export function GeneratorProvider({ children }) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const { incrementGenerationsUsed, refetchCredits } = useUser();
    const uploadInputRef = useRef(null);

    // Form State
    const [prompt, setPrompt] = useState('');
    const [logoType, setLogoType] = useState('png');
    const [transparentBackground, setTransparentBackground] = useState(false);
    const [colorHints, setColorHints] = useState([]);

    // App State
    // We keep all generated logos here to persist them across navigation
    const [logos, setLogos] = useState([]);
    const [activeLogo, setActiveLogo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefining, setIsRefining] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [limitReachedType, setLimitReachedType] = useState(null); // 'guest' | 'daily' | null

    // We no longer strictly need 'view' state as we rely on routing, 
    // but we might want to know active refinement context if needed.
    // Instead of 'refinementContext' object, we'll look up by ID from URL. 
    // However, for immediate refinement after generation, we can store temporary refinement data.
    // But to keep it simple for now, we will store the uploaded logo separately.

    const [uploadedLogo, setUploadedLogo] = useState(null);

    // Configure API client with auth
    useEffect(() => {
        configureAuth({ getAccessTokenSilently, isAuthenticated });
        setAuthenticated(isAuthenticated);
    }, [isAuthenticated, getAccessTokenSilently]);

    const handleGenerate = async () => {
        if (!prompt.trim()) return;

        setIsLoading(true);
        setError(null);
        setLimitReachedType(null);

        try {
            const sanitizedColorHints = (colorHints || [])
                .filter((h) => h?.color && (h.purpose || '').trim())
                .map((h) => ({
                    color: h.color,
                    purpose: (h.purpose || '').trim() || undefined,
                }))
                .slice(0, 3);

            const result = await generateLogos({
                prompt,
                logoType,
                transparentBackground,
                colorHints: sanitizedColorHints.length ? sanitizedColorHints : undefined,
            });
            // Prepend new logos so they are at the top, or replace? Usually replace to show fresh results.
            setLogos(result.logos);
            incrementGenerationsUsed();
            return result.logos;
        } catch (err) {
            if (err instanceof LimitExceededError) {
                setLimitReachedType(err.isGuest ? 'guest' : 'daily');
                // Don't set generic error if we have a specific limit handler
            } else {
                setError('Failed to generate logos. Please try again.');
                console.error('Generation error:', err);
            }
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefine = async (baseLogo, refinementPrompt, options = {}) => {
        setIsRefining(true);
        setLimitReachedType(null);
        try {
            const result = await refineLogo(baseLogo, refinementPrompt, options);
            setLogos(result.logos);
            return result;
        } catch (err) {
            if (err instanceof LimitExceededError) {
                setLimitReachedType(err.isGuest ? 'guest' : 'daily');
            } else {
                setError('Failed to refine logo. Please try again.');
                console.error('Refine error:', err);
            }
            return null;
        } finally {
            setIsRefining(false);
        }
    };

    const readFileAsDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });

    const loadImage = (src) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = src;
        });

    const handleUploadIcon = async (file) => {
        if (!file) return null;

        setError(null);
        const allowed = ['image/png', 'image/jpeg'];
        if (!allowed.includes(file.type)) {
            setError('Please upload a PNG or JPG image.');
            return null;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError('Image is too large. Please upload a file under 10MB.');
            return null;
        }

        setIsUploading(true);
        try {
            const dataUrl = await readFileAsDataUrl(file);
            const img = await loadImage(dataUrl);
            if (img.naturalWidth !== img.naturalHeight) {
                setError('That image is not square. Please upload a 1:1 (square) icon.');
                return null;
            }

            const uploaded = await uploadLogo(dataUrl);
            setUploadedLogo(uploaded);

            // Use local dataUrl for preview to avoid watermarking the user's own upload
            const previewVersion = { ...uploaded, url: dataUrl };
            setActiveLogo(previewVersion);

            return uploaded;
        } catch (err) {
            setError(err?.message || 'Failed to upload icon. Please try again.');
            console.error('Upload error:', err);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadBundle = async (logoId, targets = []) => {
        try {
            const result = await downloadLogoBundle(logoId, targets);

            if (result.downloadUrl) {
                window.open(result.downloadUrl, '_blank');
            }

            refetchCredits();
            return true;
        } catch (err) {
            if (err instanceof EmailNotVerifiedError) {
                throw err;
            } else if (err.name === 'InsufficientCreditsError') {
                setError(`Not enough credits. You need ${err.required} credits but only have ${err.available}.`);
            } else {
                setError('Failed to prepare download. Please try again.');
            }
            console.error('Download error:', err);
            return false;
        }
    };

    // Helper to find a logo by ID (useful for routes)
    const getLogoById = (id) => {
        return logos.find(l => String(l.id) === String(id));
    };

    const openUploadPicker = () => {
        uploadInputRef.current?.click();
    };

    const value = {
        state: {
            prompt,
            logoType,
            transparentBackground,
            colorHints,
            logos,
            activeLogo,
            isLoading,
            isRefining,
            isUploading,
            error,
            uploadedLogo,
            limitReachedType,
        },
        actions: {
            setPrompt,
            setLogoType,
            setTransparentBackground,
            setColorHints,
            setError,
            setLimitReachedType,
            handleGenerate,
            handleRefine,
            handleUploadIcon,
            handleDownloadBundle,
            getLogoById,
            openUploadPicker,
            setActiveLogo,
        },
        refs: {
            uploadInputRef,
        }
    };

    return (
        <GeneratorContext.Provider value={value}>
            {children}
        </GeneratorContext.Provider>
    );
}

export function useGenerator() {
    const context = useContext(GeneratorContext);
    if (!context) {
        throw new Error('useGenerator must be used within a GeneratorProvider');
    }
    return context;
}
